/**
 * Suppliers Page - ISO 27001 Compliant Supplier Management
 * 
 * Comprehensive third-party risk management interface with:
 * - Supplier listing with filtering
 * - Detailed supplier information sheets
 * - Add/Edit supplier dialogs
 * - Process linkage management
 * - Compliance tracking
 */

import { useState, useEffect, useMemo } from 'react';
import { 
  AlertTriangle, 
  CheckCircle, 
  Search,
  Filter,
  Plus,
  Building2,
  ShieldCheck,
  FileCheck,
  Eye,
  Edit,
  Link as LinkIcon,
  X,
  Server
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';
import {
  getAllSuppliers,
  type Supplier,
  type CriticalityRating,
  type RiskRating,
  type ComplianceStatus,
  type SupplierStatus,
} from '@/services/supplierService';

// ============================================================================
// TYPES
// ============================================================================

interface SupplierMetrics {
  totalSuppliers: number;
  activeSuppliers: number;
  criticalSuppliers: number;
  assessmentsDue: number;
  compliantSuppliers: number;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SuppliersPage() {
  const { sectionColor } = useNavigation();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'suppliers' | 'systems'>('suppliers');
  
  // State
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SupplierStatus | 'all'>('all');
  const [criticalityFilter, setCriticalityFilter] = useState<CriticalityRating | 'all'>('all');
  const [riskFilter, setRiskFilter] = useState<RiskRating | 'all'>('all');
  const [complianceFilter, setComplianceFilter] = useState<ComplianceStatus | 'all'>('all');
  
  // Selected supplier for details
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  // Add/Edit dialog
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [suppliers, searchTerm, statusFilter, criticalityFilter, riskFilter, complianceFilter]);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const response = await getAllSuppliers();
      console.log('Suppliers API response:', response);
      
      if (response.success && response.data) {
        // Handle both response structures:
        // New: { success: true, data: { suppliers: [...] } }
        // Old: { success: true, data: [...] }
        const suppliersArray = Array.isArray(response.data) 
          ? response.data 
          : response.data.suppliers;
        
        console.log('Suppliers array:', suppliersArray);
        
        if (suppliersArray && Array.isArray(suppliersArray)) {
          setSuppliers(suppliersArray);
          setFilteredSuppliers(suppliersArray);
          console.log('Suppliers set successfully:', suppliersArray.length, 'items');
        } else {
          console.warn('No valid suppliers array found', response);
          setSuppliers([]);
          setFilteredSuppliers([]);
        }
      } else {
        console.warn('No suppliers data in response', response);
        setSuppliers([]);
        setFilteredSuppliers([]);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]);
      setFilteredSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // Defensive check: ensure suppliers array exists
    if (!suppliers || !Array.isArray(suppliers)) {
      console.warn('applyFilters: suppliers array is undefined or not an array');
      setFilteredSuppliers([]);
      return;
    }

    let filtered = [...suppliers];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.identification?.supplierName?.toLowerCase().includes(search) ||
          s.identification?.tradingName?.toLowerCase().includes(search) ||
          s.serviceClassification?.serviceProvided?.toLowerCase().includes(search)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((s) => s.status === statusFilter);
    }

    // Criticality filter
    if (criticalityFilter !== 'all') {
      filtered = filtered.filter((s) => s.serviceClassification?.criticalityRating === criticalityFilter);
    }

    // Risk filter
    if (riskFilter !== 'all') {
      filtered = filtered.filter((s) => s.securityAssessment?.initialRiskScore === riskFilter);
    }

    // Compliance filter
    if (complianceFilter !== 'all') {
      filtered = filtered.filter((s) => s.complianceStatus === complianceFilter);
    }

    setFilteredSuppliers(filtered);
  };

  // ============================================================================
  // METRICS CALCULATION
  // ============================================================================

  const calculateMetrics = (): SupplierMetrics => {
    // Defensive check: ensure suppliers array exists
    if (!suppliers || !Array.isArray(suppliers)) {
      console.warn('calculateMetrics: suppliers array is undefined or not an array');
      return {
        totalSuppliers: 0,
        activeSuppliers: 0,
        criticalSuppliers: 0,
        assessmentsDue: 0,
        compliantSuppliers: 0,
      };
    }

    const activeSuppliers = suppliers.filter((s) => s.status === 'active');
    const criticalSuppliers = suppliers.filter(
      (s) => s.serviceClassification?.criticalityRating === 'critical'
    );
    const compliantSuppliers = suppliers.filter((s) => s.complianceStatus === 'compliant');
    
    // Calculate assessments due (reviews needed within 30 days)
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const assessmentsDue = suppliers.filter((s) => {
      if (!s.ongoingManagement?.nextReviewDate) return false;
      const nextReview = new Date(s.ongoingManagement.nextReviewDate);
      return nextReview <= thirtyDaysFromNow;
    });

    return {
      totalSuppliers: suppliers.length,
      activeSuppliers: activeSuppliers.length,
      criticalSuppliers: criticalSuppliers.length,
      assessmentsDue: assessmentsDue.length,
      compliantSuppliers: compliantSuppliers.length,
    };
  };

  const metrics = useMemo(() => calculateMetrics(), [suppliers]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleViewDetails = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setIsDetailsOpen(true);
  };

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setIsAddEditOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setIsAddEditOpen(true);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCriticalityFilter('all');
    setRiskFilter('all');
    setComplianceFilter('all');
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getCriticalityBadge = (criticality: CriticalityRating) => {
    const variants = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return variants[criticality];
  };

  const getRiskBadge = (risk: RiskRating) => {
    const variants = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return variants[risk];
  };

  const getComplianceBadge = (compliance: ComplianceStatus) => {
    const variants = {
      compliant: 'bg-green-500/20 text-green-400 border-green-500/30',
      'partially-compliant': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'non-compliant': 'bg-red-500/20 text-red-400 border-red-500/30',
      'under-review': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'not-assessed': 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30',
    };
    return variants[compliance];
  };

  const getStatusBadge = (status: SupplierStatus) => {
    const variants = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      inactive: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30',
      'pending-approval': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'under-review': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      terminated: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return variants[status];
  };

  // ============================================================================
  // EMPTY STATE
  // ============================================================================

  if (!loading && (!suppliers || suppliers.length === 0)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={cn('text-3xl font-bold', sectionColor)}>Supplier Risk Management</h1>
            <p className="text-neutral-400 mt-1">ISO 27001-compliant third-party risk management</p>
          </div>
          <Button onClick={handleAddSupplier} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Supplier
          </Button>
        </div>

        <Card className="p-12 text-center">
          <Building2 className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Suppliers Added Yet</h3>
          <p className="text-neutral-400 mb-6">
            Start managing your third-party vendors with comprehensive ISO 27001-compliant assessments.
          </p>
          <Button onClick={handleAddSupplier} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Supplier
          </Button>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-3xl font-bold', sectionColor)}>Suppliers & Systems</h1>
          <p className="text-neutral-400 mt-1">
            Manage third-party suppliers and in-house technology systems
          </p>
        </div>
        <Button onClick={handleAddSupplier} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          {activeTab === 'suppliers' ? 'Add Supplier' : 'Add System'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-800">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('suppliers')}
            className={cn(
              'pb-3 px-1 border-b-2 font-medium transition-colors',
              activeTab === 'suppliers'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-neutral-400 hover:text-neutral-300'
            )}
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Suppliers
            </div>
          </button>
          <button
            onClick={() => setActiveTab('systems')}
            className={cn(
              'pb-3 px-1 border-b-2 font-medium transition-colors',
              activeTab === 'systems'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-neutral-400 hover:text-neutral-300'
            )}
          >
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4" />
              Systems
            </div>
          </button>
        </div>
      </div>

      {activeTab === 'suppliers' ? (
        <>
      {/* Metrics Strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Total Suppliers</p>
              <p className="text-2xl font-bold text-white mt-1">{metrics.totalSuppliers}</p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <Building2 className="text-primary-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Active</p>
              <p className="text-2xl font-bold text-white mt-1">{metrics.activeSuppliers}</p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Critical</p>
              <p className="text-2xl font-bold text-danger-500 mt-1">{metrics.criticalSuppliers}</p>
            </div>
            <div className="p-3 bg-danger-500/20 rounded-lg">
              <AlertTriangle className="text-danger-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Reviews Due</p>
              <p className="text-2xl font-bold text-warning-500 mt-1">{metrics.assessmentsDue}</p>
            </div>
            <div className="p-3 bg-warning-500/20 rounded-lg">
              <FileCheck className="text-warning-500" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400">Compliant</p>
              <p className="text-2xl font-bold text-success-500 mt-1">{metrics.compliantSuppliers}</p>
            </div>
            <div className="p-3 bg-success-500/20 rounded-lg">
              <ShieldCheck className="text-success-500" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as SupplierStatus | 'all')}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending-approval">Pending Approval</SelectItem>
              <SelectItem value="under-review">Under Review</SelectItem>
              <SelectItem value="terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>

          <Select value={criticalityFilter} onValueChange={(value) => setCriticalityFilter(value as CriticalityRating | 'all')}>
            <SelectTrigger>
              <SelectValue placeholder="Criticality" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Criticality</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          <Select value={riskFilter} onValueChange={(value) => setRiskFilter(value as RiskRating | 'all')}>
            <SelectTrigger>
              <SelectValue placeholder="Risk" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Risk Levels</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-neutral-400">
            Showing {filteredSuppliers?.length || 0} of {suppliers?.length || 0} suppliers
          </p>
          {(searchTerm || statusFilter !== 'all' || criticalityFilter !== 'all' || riskFilter !== 'all' || complianceFilter !== 'all') && (
            <Button onClick={handleResetFilters} variant="ghost" size="sm">
              <X className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Suppliers Table */}
      <Card>
        <div className="card-header">
          <h2 className="text-lg font-semibold">Supplier Registry</h2>
        </div>
        <div className="overflow-x-auto p-6">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Supplier</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Service</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Criticality</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Risk</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Compliance</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Status</th>
                <th className="text-right p-4 text-sm font-medium text-neutral-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((supplier) => (
                <tr key={supplier.id} className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                  <td className="p-4">
                    <div>
                      <p className="text-sm font-medium text-white">{supplier.identification.supplierName}</p>
                      {supplier.identification.tradingName && (
                        <p className="text-xs text-neutral-400">({supplier.identification.tradingName})</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-sm text-neutral-300">{supplier.serviceClassification.serviceProvided}</p>
                    <p className="text-xs text-neutral-500 capitalize">
                      {supplier.serviceClassification.serviceCategory.replace(/-/g, ' ')}
                    </p>
                  </td>
                  <td className="p-4">
                    <Badge className={cn('capitalize', getCriticalityBadge(supplier.serviceClassification.criticalityRating))}>
                      {supplier.serviceClassification.criticalityRating}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge className={cn('capitalize', getRiskBadge(supplier.securityAssessment.initialRiskScore))}>
                      {supplier.securityAssessment.initialRiskScore}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge className={cn('capitalize', getComplianceBadge(supplier.complianceStatus))}>
                      {supplier.complianceStatus.replace(/-/g, ' ')}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge className={cn('capitalize', getStatusBadge(supplier.status))}>
                      {supplier.status.replace(/-/g, ' ')}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        onClick={() => handleViewDetails(supplier)}
                        variant="ghost"
                        size="sm"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleEditSupplier(supplier)}
                        variant="ghost"
                        size="sm"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSuppliers.length === 0 && (
            <div className="text-center py-12">
              <Filter className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
              <p className="text-neutral-400">No suppliers match your filters</p>
            </div>
          )}
        </div>
      </Card>

      {/* Supplier Details Sheet */}
      {selectedSupplier && (
        <Sheet open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{selectedSupplier.identification.supplierName}</SheetTitle>
              <SheetDescription>
                {selectedSupplier.serviceClassification.serviceProvided}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge className={cn('capitalize', getStatusBadge(selectedSupplier.status))}>
                  {selectedSupplier.status.replace(/-/g, ' ')}
                </Badge>
                <Badge className={cn('capitalize', getCriticalityBadge(selectedSupplier.serviceClassification.criticalityRating))}>
                  {selectedSupplier.serviceClassification.criticalityRating} criticality
                </Badge>
                <Badge className={cn('capitalize', getRiskBadge(selectedSupplier.securityAssessment.initialRiskScore))}>
                  {selectedSupplier.securityAssessment.initialRiskScore} risk
                </Badge>
                <Badge className={cn('capitalize', getComplianceBadge(selectedSupplier.complianceStatus))}>
                  {selectedSupplier.complianceStatus.replace(/-/g, ' ')}
                </Badge>
              </div>

              {/* Identification */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Identification</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Company Number:</span>
                    <span className="text-white">{selectedSupplier.identification.companyNumber || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Country:</span>
                    <span className="text-white">{selectedSupplier.identification.country}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Primary Contact:</span>
                    <span className="text-white">{selectedSupplier.identification.primaryContactName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Contact Email:</span>
                    <span className="text-white">{selectedSupplier.identification.primaryContactEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Business Owner:</span>
                    <span className="text-white">{selectedSupplier.identification.businessOwnerInternal}</span>
                  </div>
                </div>
              </div>

              {/* Service Classification */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Service Classification</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Service Category:</span>
                    <span className="text-white capitalize">{selectedSupplier.serviceClassification.serviceCategory.replace(/-/g, ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Data Processing:</span>
                    <span className="text-white">{selectedSupplier.serviceClassification.dataProcessingInvolved ? 'Yes' : 'No'}</span>
                  </div>
                  {selectedSupplier.serviceClassification.dataProcessingLocation && (
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Processing Location:</span>
                      <span className="text-white">{selectedSupplier.serviceClassification.dataProcessingLocation}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Sub-processors:</span>
                    <span className="text-white">{selectedSupplier.serviceClassification.thirdPartySubProcessors ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Security Assessment */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Security Assessment</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">ISO 27001 Certified:</span>
                    <span className="text-white">{selectedSupplier.securityAssessment.iso27001Certified ? 'Yes' : 'No'}</span>
                  </div>
                  {selectedSupplier.securityAssessment.iso27001CertificateExpiry && (
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Certificate Expiry:</span>
                      <span className="text-white">{new Date(selectedSupplier.securityAssessment.iso27001CertificateExpiry).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Pen Test Evidence:</span>
                    <span className="text-white">{selectedSupplier.securityAssessment.penTestEvidence ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Cyber Insurance:</span>
                    <span className="text-white">{selectedSupplier.securityAssessment.cyberInsuranceCoverage ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Risk Owner:</span>
                    <span className="text-white">{selectedSupplier.securityAssessment.riskOwnerInternal}</span>
                  </div>
                </div>
              </div>

              {/* Contractual Controls */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Contractual Controls</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Contract in Place:</span>
                    <span className="text-white">{selectedSupplier.contractualControls.contractInPlace ? 'Yes' : 'No'}</span>
                  </div>
                  {selectedSupplier.contractualControls.contractEndDate && (
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Contract End Date:</span>
                      <span className="text-white">{new Date(selectedSupplier.contractualControls.contractEndDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-neutral-400">DPA Signed:</span>
                    <span className="text-white">{selectedSupplier.contractualControls.dataProcessingAgreement ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Right to Audit:</span>
                    <span className="text-white">{selectedSupplier.contractualControls.rightToAuditClause ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">SLA in Place:</span>
                    <span className="text-white">{selectedSupplier.contractualControls.serviceLevelAgreement ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Ongoing Management */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">Ongoing Management</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Review Frequency:</span>
                    <span className="text-white capitalize">{selectedSupplier.ongoingManagement.performanceReviewFrequency}</span>
                  </div>
                  {selectedSupplier.ongoingManagement.lastReviewDate && (
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Last Review:</span>
                      <span className="text-white">{new Date(selectedSupplier.ongoingManagement.lastReviewDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedSupplier.ongoingManagement.nextReviewDate && (
                    <div className="flex justify-between">
                      <span className="text-neutral-400">Next Review:</span>
                      <span className="text-white">{new Date(selectedSupplier.ongoingManagement.nextReviewDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Incidents Logged:</span>
                    <span className="text-white">{selectedSupplier.ongoingManagement.issuesOrIncidentsLogged ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Linked Processes */}
              {selectedSupplier.linkedProcesses.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3">Linked Processes</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedSupplier.linkedProcesses.map((processId) => (
                      <Badge key={processId} variant="outline" className="border-primary-500/30 text-primary-400">
                        <LinkIcon className="w-3 h-3 mr-1" />
                        {processId}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-neutral-800">
                <Button onClick={() => handleEditSupplier(selectedSupplier)} className="flex-1 btn-primary">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Supplier
                </Button>
                <Button variant="outline" className="flex-1">
                  <FileCheck className="w-4 h-4 mr-2" />
                  View Assessments
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Add/Edit Dialog - Placeholder for now */}
      <Dialog open={isAddEditOpen} onOpenChange={setIsAddEditOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
            <DialogDescription>
              {editingSupplier
                ? 'Update supplier information and ISO 27001 compliance details'
                : 'Add a new supplier with comprehensive ISO 27001 compliance tracking'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <p className="text-neutral-400 text-center">
              Add/Edit form coming soon - comprehensive form with all ISO 27001 fields
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddEditOpen(false)}>
              Cancel
            </Button>
            <Button className="btn-primary" disabled>
              {editingSupplier ? 'Save Changes' : 'Create Supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      ) : (
        /* Systems Tab */
        <div className="space-y-6">
          <Card className="p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-primary-500/20 rounded-lg">
                <Server className="w-12 h-12 text-primary-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Systems Management</h3>
                <p className="text-neutral-400 max-w-2xl">
                  Track your in-house built technology systems, architecture diagrams, 
                  source control repositories, and MCP server integrations.
                </p>
              </div>
              <Button className="btn-primary mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First System
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
